import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import {
    MOBILE_NAV_MEDIA,
    SHORT_VIEWPORT_MEDIA,
} from "../layout/layoutConstants";
import { LOBBY_EXIT_BUTTON_SX } from "../common/dialogActionButtonSx";

export const MOBILE_NAV_MQ = `@media ${MOBILE_NAV_MEDIA}`;
export const SHORT_VIEWPORT_MQ = `@media ${SHORT_VIEWPORT_MEDIA}`;

/** Normal-flow spacing for the V2 lobby command spine. */
export const LOBBY_PARTY_TO_PLAY_GAP_SX = {
    xs: "1.35rem",
    xl: "1.6rem",
};
export const LOBBY_PARTY_TO_PLAY_GAP_MOBILE = "1.5rem";
export const LOBBY_PARTY_TO_PLAY_GAP_SHORT = "0.8rem";

/** Keeps conditional queue guidance from shifting the setup deck. */
export const LOBBY_GUIDANCE_SLOT_HEIGHT_SX = {
    xs: "2.65rem",
    xl: "2.85rem",
};
export const LOBBY_GUIDANCE_SLOT_HEIGHT_MOBILE = "3rem";
export const LOBBY_GUIDANCE_SLOT_HEIGHT_SHORT = "2.5rem";

export const LOBBY_SETUP_DECK_MAX_WIDTH = "68rem";

export const LOBBY_SETUP_DECK_GAP_SX = {
    xs: "0.7rem",
    xl: "0.85rem",
};
export const LOBBY_SETUP_DECK_GAP_MOBILE = "0.65rem";
export const LOBBY_SETUP_DECK_GAP_SHORT = "0.5rem";

/** Premium-black surface language shared with the profile experience. */
export const LOBBY_OURO_SURFACE = `
    linear-gradient(
        180deg,
        rgba(12, 12, 14, 0.96) 0%,
        rgba(8, 8, 10, 0.985) 100%
    )
`;
export const LOBBY_OURO_BORDER = "rgba(255, 255, 255, 0.055)";
export const LOBBY_OURO_SHADOW = `
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    inset 0 -40px 80px rgba(246, 185, 76, 0.03),
    0 24px 60px rgba(0, 0, 0, 0.42)
`;
export const LOBBY_OURO_TOP_GLOW = `
    linear-gradient(
        90deg,
        transparent,
        rgba(246, 185, 76, 0.95),
        transparent
    )
`;

/** Shared party-slot card dimensions — self, other players, and empty slots. */
export const LOBBY_PARTY_CARD_SX = {
    width: { xs: "8.9rem", xl: "10.8rem" },
    height: { xs: "12.1rem", xl: "14.6rem" },
    flexShrink: 0,
    boxSizing: "border-box",
    [MOBILE_NAV_MQ]: {
        width: "10.7rem",
        height: "13.4rem",
        maxWidth: "100%",
    },
};

const CORNER_GOLD = "rgba(120, 82, 28, 0.42)";
const CORNER_GOLD_INNER = "rgba(140, 96, 36, 0.22)";
const CORNER_GOLD_GLOW = `
    drop-shadow(0 0 4px rgba(90, 58, 14, 0.18))
`;

export const CORNER_BRACKET_SX = {
    position: "absolute",
    width: "22px",
    height: "22px",
    pointerEvents: "none",
    zIndex: 6,
    opacity: 1,
    boxSizing: "border-box",
    color: CORNER_GOLD,
    transition: `
        opacity 220ms ease,
        filter 220ms ease
    `,
    filter: CORNER_GOLD_GLOW,
    // Quiet inner rail for depth
    "&::before": {
        content: '""',
        position: "absolute",
        pointerEvents: "none",
        opacity: 0.7,
    },
    // Small dark joint marker — barely there
    "&::after": {
        content: '""',
        position: "absolute",
        width: 3,
        height: 3,
        pointerEvents: "none",
        background: `
            linear-gradient(
                145deg,
                rgba(150, 100, 32, 0.55) 0%,
                rgba(90, 58, 14, 0.75) 100%
            )
        `,
        clipPath: "polygon(50% 0, 100% 50%, 50% 100%, 0 50%)",
        boxShadow: `0 0 4px rgba(90, 58, 14, 0.25)`,
    },
    [MOBILE_NAV_MQ]: {
        display: "none",
        width: "14px",
        height: "14px",
        filter: `drop-shadow(0 0 3px rgba(90, 58, 14, 0.16))`,
        "&::after": {
            width: 2,
            height: 2,
        },
    },
};

export const CORNER_BRACKET_TL_SX = {
    top: 0,
    left: 0,
    borderTop: `2px solid ${CORNER_GOLD}`,
    borderLeft: `2px solid ${CORNER_GOLD}`,
    borderRadius: "6px 0 0 0",
    "&::before": {
        top: 3,
        left: 3,
        width: "13px",
        height: "13px",
        borderTop: `1px solid ${CORNER_GOLD_INNER}`,
        borderLeft: `1px solid ${CORNER_GOLD_INNER}`,
        borderRadius: "4px 0 0 0",
    },
    "&::after": {
        top: 1,
        left: 1,
    },
    [MOBILE_NAV_MQ]: {
        display: "none",
        borderRadius: "6px 0 0 0",
        "&::before": {
            top: 2,
            left: 2,
            width: "9px",
            height: "9px",
            borderRadius: "4px 0 0 0",
        },
    },
};

export const CORNER_BRACKET_TR_SX = {
    top: 0,
    right: 0,
    borderTop: `2px solid ${CORNER_GOLD}`,
    borderRight: `2px solid ${CORNER_GOLD}`,
    borderRadius: "0 6px 0 0",
    "&::before": {
        top: 3,
        right: 3,
        width: "13px",
        height: "13px",
        borderTop: `1px solid ${CORNER_GOLD_INNER}`,
        borderRight: `1px solid ${CORNER_GOLD_INNER}`,
        borderRadius: "0 4px 0 0",
    },
    "&::after": {
        top: 1,
        right: 1,
    },
    [MOBILE_NAV_MQ]: {
        display: "none",
        borderRadius: "0 6px 0 0",
        "&::before": {
            top: 2,
            right: 2,
            width: "9px",
            height: "9px",
            borderRadius: "0 4px 0 0",
        },
    },
};

export const CORNER_BRACKET_BL_SX = {
    bottom: 0,
    left: 0,
    borderBottom: `2px solid ${CORNER_GOLD}`,
    borderLeft: `2px solid ${CORNER_GOLD}`,
    borderRadius: "0 0 0 6px",
    "&::before": {
        bottom: 3,
        left: 3,
        width: "13px",
        height: "13px",
        borderBottom: `1px solid ${CORNER_GOLD_INNER}`,
        borderLeft: `1px solid ${CORNER_GOLD_INNER}`,
        borderRadius: "0 0 0 4px",
    },
    "&::after": {
        bottom: 1,
        left: 1,
    },
    [MOBILE_NAV_MQ]: {
        display: "none",
        borderRadius: "0 0 0 6px",
        "&::before": {
            bottom: 2,
            left: 2,
            width: "9px",
            height: "9px",
            borderRadius: "0 0 0 4px",
        },
    },
};

export const CORNER_BRACKET_BR_SX = {
    bottom: 0,
    right: 0,
    borderBottom: `2px solid ${CORNER_GOLD}`,
    borderRight: `2px solid ${CORNER_GOLD}`,
    borderRadius: "0 0 6px 0",
    "&::before": {
        bottom: 3,
        right: 3,
        width: "13px",
        height: "13px",
        borderBottom: `1px solid ${CORNER_GOLD_INNER}`,
        borderRight: `1px solid ${CORNER_GOLD_INNER}`,
        borderRadius: "0 0 4px 0",
    },
    "&::after": {
        bottom: 1,
        right: 1,
    },
    [MOBILE_NAV_MQ]: {
        display: "none",
        borderRadius: "0 0 6px 0",
        "&::before": {
            bottom: 2,
            right: 2,
            width: "9px",
            height: "9px",
            borderRadius: "0 0 4px 0",
        },
    },
};

export const SEARCHING_BRACKET_ANIMATION = {
    initial: {
        opacity: 1,
        scale: 1,
        filter: CORNER_GOLD_GLOW.trim().replace(/\s+/g, " "),
    },

    animate: {
        opacity: [0.75, 1, 0.82],
        scale: [1, 1.01, 1],
        filter: [
            "drop-shadow(0 0 3px rgba(90,58,14,0.14))",
            "drop-shadow(0 0 6px rgba(140,96,36,0.32))",
            "drop-shadow(0 0 4px rgba(110,72,20,0.2))",
        ],
    },

    transition: {
        duration: 3.4,
        repeat: Infinity,
        ease: "easeInOut",
    },
};

export const LOBBY_NOTIFY_PLAYER_BASE_SX = {
    position: "fixed",
    bottom: "1rem",
    right: 0,
    zIndex: 10000,
    borderRadius: "8px 0 0 8px",
    padding: { xs: "1rem 2rem", xl: "1.2rem 2.1rem" },
    boxShadow: "none",
    border: "none",
    minWidth: { xs: "300px", xl: "350px" },
    maxWidth: "100%",
    "@keyframes lobbyPlayerJoinedSlideIn": {
        "0%": { transform: "translateX(100%)", opacity: 0 },
        "100%": { transform: "translateX(0)", opacity: 1 },
    },
    animation: "lobbyPlayerJoinedSlideIn 0.5s ease-out",
};

export const LOBBY_NOTIFY_PLAYER_JOINED_SX = {
    backgroundColor: colors.state.success.main,
    boxShadow: `0 4px 20px ${alpha(colors.state.success.main, 0.3)}`,
    border: `1px solid ${alpha(colors.state.success.main, 0.5)}`,
};

export const LOBBY_NOTIFY_PLAYER_LEFT_SX = {
    backgroundColor: colors.state.danger.main,
    boxShadow: `0 4px 20px ${alpha(colors.state.danger.main, 0.3)}`,
    border: `1px solid ${alpha(colors.state.danger.main, 0.5)}`,
};

export const LOBBY_PLAYER_NOTIFICATION_TEXT_SX = {
    color: colors.text.primary,
    fontSize: { xs: "0.9rem", xl: "1.2rem" },
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
    whiteSpace: "nowrap",
};

export const UNAVAILABLE_ACTION_MESSAGE_SX = {
    position: "absolute",
    top: { xs: "20%", xl: "24%" },
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 10000,
    background: "linear-gradient(135deg, #1C1E20 0%, #2A2D30 100%)",
    padding: { xs: "0.4rem 1.5rem", xl: "0.6rem 1.8rem" },
    borderRadius: "6px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
};

export const UNAVAILABLE_ACTION_MESSAGE_TEXT_SX = {
    color: colors.text.primary,
    fontSize: { xs: "0.9rem", xl: "1.2rem" },
    fontWeight: 500,
    textAlign: "center",
};

export const LOBBY_TOP_BAR_ICON_BUTTON_SX = {
    width: { xs: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)", xl: "3.1rem" },
    height: { xs: "clamp(2.35rem, 2.15rem + 0.8vw, 2.9rem)", xl: "3.1rem" },
    backgroundColor: "rgba(28, 30, 32, 0.85)",
    backdropFilter: "blur(8px)",
    transition: "all 0.15s ease-in-out",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    "&:hover": {
        backgroundColor: "rgba(28, 30, 32, 1)",
        transform: "scale(1.05)",
        boxShadow:
            "0 0 12px rgba(91, 33, 230, 0.35), 0 0 4px rgba(91, 33, 230, 0.25)",
        borderColor: "rgba(91, 33, 230, 0.3)",
    },
};

export const LOBBY_TOP_BAR_ICON_SX = {
    fontSize: { xs: "clamp(1.05rem, 0.95rem + 0.35vw, 1.3rem)", xl: "1.45rem" },
    color: colors.text.primary,
};

export { LOBBY_EXIT_BUTTON_SX };

export const LOBBY_EXIT_BUTTON_BLOCKED_SX = {
    background: `
        linear-gradient(
            180deg,
            rgba(140, 32, 32, 0.72) 0%,
            rgba(92, 18, 18, 0.72) 100%
        )
    `,
    color: colors.text.primary,
    "&:hover": {
        background: `
            linear-gradient(
                180deg,
                rgba(152, 38, 38, 0.85) 0%,
                rgba(104, 22, 22, 0.85) 100%
            )
        `,
        transform: "translateY(-1px) scale(1.01)",
        boxShadow: `
            0 1px 0 rgba(255,255,255,0.07) inset,
            0 -1px 0 rgba(0,0,0,0.3) inset,
            0 10px 24px rgba(0,0,0,0.38)
        `,
        "&::after": {
            opacity: 0.85,
        },
    },
    "&:active": {
        transform: "translateY(1px) scale(0.992)",
    },
};
